import { useIntl } from '@mfw/components';

import {
  usePermissionsContext,
  useNavigationContext,
  useAuthContext,
  useOrganizationContext,
} from '../../../../context';
import { useBuildRoute } from '../../../../router/hooks';
import SidebarSection, {
  type SidebarSectionItem,
} from '../SidebarSection/SidebarSection';
import messages from '../messages';

export interface SidebarPlatformAdminSectionProps {
  onNavigate: () => void;
}

/**
 * The platform-user block, rendered ABOVE the org drill-down sections so a
 * platform user reaches it without scrolling past them. Two groups, split by
 * who may see them:
 *
 * - "Tenant admin" — both platform roles (P4: `platform_admin` within its own
 *   tenant, `platform_super_admin` within the selected one).
 * - "Platform" — cross-tenant, `isSuperAdmin` only. The flag decides, never a
 *   permission key: after the P5 bypass every platform user holds all of them.
 */
const SidebarPlatformAdminSection = ({
  onNavigate,
}: SidebarPlatformAdminSectionProps) => {
  const { currentUser } = useAuthContext();
  const { hasPermission } = usePermissionsContext();
  const { isMiniBarAvailable } = useNavigationContext();
  const { formatMessage } = useIntl();
  const { of } = useBuildRoute();
  const { currentOrganization, selectPersonalProfile } =
    useOrganizationContext();

  // F4 (platform-user-class): after the P5 bypass every platform user holds
  // every permission — the old per-permission OR collapsed to the class flag.
  if (!currentUser?.isPlatformUser) return null;

  const soonLabel = formatMessage(messages.soon);
  const persistKey = (id: string) => `${currentUser.id}:platform:${id}`;

  // Org-less authoring workspace (P6): content created from these rows has no
  // organization. A platform user holds no membership, so the org Library
  // section never shows up for them without a selected org.
  const libraryItems: SidebarSectionItem[] = [
    {
      label: formatMessage(messages.workoutContent),
      icon: 'mdi:dumbbell',
      to: of('/library', { search: 'tab=workouts' }),
      testId: 'sidebar-admin-library-workout-content',
    },
    {
      label: formatMessage(messages.nutrition),
      icon: 'mdi:food-apple-outline',
      to: of('/nutrition'),
      testId: 'sidebar-admin-library-nutrition',
    },
    {
      label: formatMessage(messages.education),
      icon: 'mdi:school-outline',
      to: of('/education'),
      testId: 'sidebar-admin-library-education',
    },
  ];

  // These rows ARE the org-less workspace (P6). With a customer org still
  // selected, the pages behind them read — and create in — that org, so leave
  // it first: the same transition as the header's "Back to workspace".
  const onLibraryNavigate = () => {
    if (currentOrganization) selectPersonalProfile();
    onNavigate();
  };

  const tenantAdminItems: SidebarSectionItem[] = [
    ...(hasPermission('organizations_read')
      ? [
          {
            label: formatMessage(messages.organizations),
            icon: 'clarity:organization-line',
            to: of('/admin/organizations'),
            testId: 'sidebar-admin-organizations',
          },
        ]
      : []),
    ...(hasPermission('roles_read')
      ? [
          {
            label: formatMessage(messages.rolesAndPermissions),
            icon: 'fluent-mdl2:permissions',
            to: of('/admin/roles'),
            testId: 'sidebar-admin-roles-and-permissions',
          },
        ]
      : []),
    ...(hasPermission('categories_read')
      ? [
          {
            label: formatMessage(messages.categories),
            icon: 'mdi:category',
            to: of('/admin/categories'),
            testId: 'sidebar-admin-categories',
          },
        ]
      : []),
    ...(hasPermission('finance_stats_read')
      ? [
          {
            label: formatMessage(messages.adminFinance),
            icon: 'mdi:cash-multiple',
            to: of('/admin/finance'),
            testId: 'sidebar-admin-finance',
          },
        ]
      : []),
    // F6 (Task 19): the takedown switch is NOT super-admin-only — both
    // platform roles may block a page within their own tenant (P4/P5).
    // The BE route is onlyPlatformUser-only, no permission key, so the
    // class flag alone decides here too.
    {
      label: formatMessage(messages.publicPages),
      icon: 'mdi:web',
      to: of('/admin/public-pages'),
      testId: 'sidebar-admin-public-pages',
    },
    // 6.8: onlyPlatformUser route, no permission key (P5) — same gate as above
    {
      label: formatMessage(messages.marketplaceAccess),
      icon: 'mdi:store-remove-outline',
      to: of('/admin/marketplace-access'),
      testId: 'sidebar-admin-marketplace-access',
    },
    ...(hasPermission('privacy_policy_read')
      ? [
          {
            label: formatMessage(messages.privacyPolicies),
            icon: 'mdi:shield-account-outline',
            to: of('/admin/privacy-policies'),
            testId: 'sidebar-admin-privacy-policies',
          },
        ]
      : []),
    ...(hasPermission('org_rebrand_manage')
      ? [
          {
            label: formatMessage(messages.tenantAppBranding),
            icon: 'mdi:palette-outline',
            to: of('/admin/rebrand'),
            testId: 'sidebar-admin-rebrand',
          },
        ]
      : []),
  ];

  // F1b/F1c (§6b-5): tenants, corp organizations and /admin/stats are all
  // cross-tenant — the BE routes are onlySuperAdmin, so the flag decides.
  const platformItems: SidebarSectionItem[] = currentUser.isSuperAdmin
    ? [
        {
          label: formatMessage(messages.tenants),
          icon: 'ph:google-cardboard-logo',
          to: of('/admin/tenants'),
          testId: 'sidebar-admin-tenants',
        },
        {
          label: formatMessage(messages.corpOrganizations),
          icon: 'clarity:building-line',
          to: of('/admin/corp-organizations'),
          testId: 'sidebar-admin-corp-organizations',
        },
        {
          label: formatMessage(messages.stats),
          icon: 'mdi:chart-areaspline',
          to: of('/admin/stats'),
          testId: 'sidebar-admin-stats',
        },
      ]
    : [];

  return (
    <>
      {/* Unlabeled on purpose: the tenant-admin rows are standalone items, not
          a collapsible group. */}
      <SidebarSection
        id="tenant-admin"
        items={tenantAdminItems}
        persistKey={persistKey('tenant-admin')}
        isMiniBarAvailable={isMiniBarAvailable}
        onNavigate={onNavigate}
        soonLabel={soonLabel}
      />
      <SidebarSection
        id="platform-library"
        label={formatMessage(messages.library)}
        items={libraryItems}
        persistKey={persistKey('library')}
        isMiniBarAvailable={isMiniBarAvailable}
        onNavigate={onLibraryNavigate}
        soonLabel={soonLabel}
      />
      <SidebarSection
        id="platform"
        label={formatMessage(messages.platform)}
        items={platformItems}
        persistKey={persistKey('platform')}
        isMiniBarAvailable={isMiniBarAvailable}
        onNavigate={onNavigate}
        soonLabel={soonLabel}
      />
    </>
  );
};

export default SidebarPlatformAdminSection;
